import {
    FunctionComponent,
    useState,
    useEffect,
    useRef,
    useCallback
} from 'react';
import { Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSelector } from 'react-redux';
import { MarkdownEditor } from 'src/shared/MarkdownEditor';

import { ReactComponent as GithubIcon } from 'src/assets/icons/github.svg';
import { ReactComponent as CloseIcon } from 'src/assets/icons/close-icon-filled.svg';
import { ReactComponent as CheckIcon } from 'src/assets/icons/check.svg';
import { ReactComponent as AddIcon } from 'src/assets/icons/add.svg';
import { ReactComponent as AttachmentIcon } from 'src/assets/icons/attachment.svg';

import color from 'src/theme/color';

import useFileUploadWithValidation, {
    UploadedFile
} from 'src/hooks/useFileUploadWithValidation';
import { generateUniqueId } from 'src/utils/attachments';
import AttachmentItem from 'src/shared/AttachmentItem';
import UploadProgress from 'src/shared/UploadProgress';

import { Formik, Form } from 'formik';

import {
    ServicesCard,
    IconButtonStyled,
    ButtonContentBox,
    TitleContainer,
    StyledTextField,
    FlexContainer,
    SkillChip,
    AddButton,
    ConnectChipActive,
    TeamMemberChip,
    TeamMemberRole,
    IconStyle,
    AddIconStyle,
    CloseIconStyle,
    MDEditorContainer,
    AttachmentsContainer,
    HiddenFileInput,
    AttachmentIconButton
} from './styled';
import SkillDialog from './SkillDialogue';

import useAppDispatch from '../../../../hooks/useAppDispatch';

import { ProjectMember } from 'src/types/onboarding';
import { UpdateProjectRequest } from 'src/types/projects';

import {
    StyledButton,
    TitleBox,
    TitleText,
    ButtonTextBox,
    ButtonIconBox
} from '../styled';

import { LabelTypography, InputField, StyledDivider } from '../../../styled';

import { FormRow, FormField } from '../../../Account/styled';

import { updateProject } from '../../../../redux/onboarding/service';
import { updateProjectDetails } from '../../../../redux/onboarding/slice';
import { getProjectDetailsFromState } from '../../../../redux/onboarding/selectors';

import { TeamMember, ProjectSkill } from '../../../../types/projects';

interface ProjectDetailsProps {
    setCurrentStep: (step: number) => void;
    projectId: number;
    skills: ProjectSkill[];
    setSkills: (skills: ProjectSkill[]) => void;
    teamMembers: TeamMember[];
    setTeamMembers: (teamMembers: TeamMember[]) => void;
}

const ProjectDetails: FunctionComponent<ProjectDetailsProps> = ({
    setCurrentStep,
    projectId,
    skills,
    setSkills,
    teamMembers,
    setTeamMembers
}) => {
    const dispatch = useAppDispatch();

    const projectDetails = useSelector(getProjectDetailsFromState);

    const [hoveredSkill, setHoveredSkill] = useState<number | null>(null);
    const [hoveredTeam, setHoveredTeam] = useState<number | null>(null);
    const [isCreateHovered, setCreateIsHovered] = useState(false);
    const [isAddSkillHovered, setIsAddSkillHovered] = useState(false);
    const [isAddTeamHovered, setIsAddTeamHovered] = useState(false);

    const [skillDialogOpen, setSkillDialogOpen] = useState(false);
    const [connected, setConnected] = useState(false);

    const [files, setFiles] = useState<UploadedFile[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFilesSelect = useFileUploadWithValidation({
        onFilesUpdate: setFiles,
        onError: (error) => console.error('File upload error:', error)
    });

    const handleAddSkills = (newSkills: ProjectSkill[]) => {
        setSkills([...skills, ...newSkills]);
    };

    const handleRemoveSkill = (indexToRemove: number) => {
        setSkills(skills.filter((_, index) => index !== indexToRemove));
    };

    const handleRemoveTeamMember = (indexToRemove: number) => {
        setTeamMembers(
            teamMembers.filter((_, index) => index !== indexToRemove)
        );
    };

    const handleFileChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.files && e.target.files.length > 0) {
                const selectedFilesWithIds = Array.from(e.target.files).map(
                    (file) => ({
                        file,
                        id: generateUniqueId()
                    })
                );
                handleFilesSelect(selectedFilesWithIds);
            }
            // Reset the input so the same file can be selected again
            if (e.target) {
                e.target.value = '';
            }
        },
        [handleFilesSelect]
    );

    const handleAttachmentClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const removeAttachment = useCallback((idToRemove: number): void => {
        setFiles((prevFiles) =>
            prevFiles.filter((file) => {
                // Match by fileId if it exists, otherwise match by local id
                if (file.fileId) {
                    return file.fileId !== idToRemove;
                }
                return file.id !== idToRemove;
            })
        );
    }, []);

    const cancelUpload = useCallback(
        (fileId: number): void => {
            const fileToCancel = files.find((f) => f.id === fileId);

            if (fileToCancel && fileToCancel.uploadController) {
                fileToCancel.uploadController.abort();
            }

            removeAttachment(fileId);
        },
        [files, removeAttachment]
    );

    useEffect(() => {
        const isGithubConnected = projectDetails.details.githubConnected;
        if (isGithubConnected) {
            setConnected(isGithubConnected);
        }
    }, [projectDetails]);

    const handleCreateProject = async () => {
        try {
            const members: ProjectMember[] = teamMembers
                .filter((member) => member.role !== 'Remove')
                .map((member) => {
                    return {
                        userId: member.id,
                        role: member.role as 'Admin' | 'Member'
                    };
                });

            // Get attachment IDs from uploaded files
            const attachmentIds = files
                .filter((f) => f.fileId && f.status === 'completed')
                .map((f) => Number(f.fileId));

            const projectInfoApi: UpdateProjectRequest = {
                projectId,
                name: projectDetails.details.projectName,
                description: projectDetails.details.description,
                skills: skills.map((skill) => skill.id),
                members,
                attachmentList: attachmentIds
            };

            await dispatch(updateProject(projectInfoApi));
            setCurrentStep(4);
        } catch (error) {}
    };

    const handleFieldChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        if (name === 'projectName') {
            dispatch(
                updateProjectDetails({
                    projectName: value
                })
            );
        }
    };

    const handleDescriptionChange = (value: string | undefined) => {
        dispatch(
            updateProjectDetails({
                description: value || ''
            })
        );
    };

    return (
        <ServicesCard>
            <TitleBox>
                <TitleContainer>
                    <IconButtonStyled
                        aria-label="back"
                        onClick={() => {
                            setCurrentStep(1);
                        }}
                        sx={{ position: 'absolute', left: 0 }}
                    >
                        <ArrowBackIcon fontSize="small" />
                    </IconButtonStyled>
                    <Typography
                        variant="body1"
                        color={color.white}
                        component={TitleText}
                        sx={{
                            width: '100%',
                            textAlign: 'center'
                        }}
                    >
                        Project details
                    </Typography>
                </TitleContainer>
            </TitleBox>
            <StyledDivider />

            <Formik
                initialValues={{
                    projectName: projectDetails.details.projectName || '',
                    description: projectDetails.details.description || '',
                    attachmentList: projectDetails.details.attachmentList || []
                }}
                enableReinitialize={true}
                onSubmit={handleCreateProject}
            >
                {({ values, handleChange }) => (
                    <Form>
                        <FormRow>
                            <FormField>
                                <LabelTypography sx={{ textAlign: 'left' }}>
                                    Project name
                                </LabelTypography>
                                <StyledTextField
                                    fullWidth
                                    name="projectName"
                                    value={values.projectName}
                                    onChange={(e) => {
                                        handleChange(e);
                                        handleFieldChange(e);
                                    }}
                                    variant="outlined"
                                    required
                                    InputProps={{ sx: InputField }}
                                />
                            </FormField>
                        </FormRow>

                        <FormRow>
                            <FormField>
                                <LabelTypography sx={{ textAlign: 'left' }}>
                                    Description
                                </LabelTypography>
                                <MDEditorContainer>
                                    <MarkdownEditor
                                        value={values.description}
                                        onChange={(v) => {
                                            handleChange({
                                                target: {
                                                    name: 'description',
                                                    value: v || ''
                                                }
                                            } as any);
                                            handleDescriptionChange(v);
                                        }}
                                        height={200}
                                    />
                                </MDEditorContainer>
                            </FormField>
                        </FormRow>

                        <FormRow>
                            <FormField>
                                <LabelTypography sx={{ textAlign: 'left' }}>
                                    Attachments
                                </LabelTypography>

                                {/* Hidden file input */}
                                <HiddenFileInput
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx"
                                    multiple
                                    onChange={handleFileChange}
                                />

                                {/* Attachments container with icon button */}
                                <AttachmentsContainer>
                                    <AttachmentIconButton
                                        onClick={handleAttachmentClick}
                                        type="button"
                                    >
                                        <AttachmentIcon />
                                    </AttachmentIconButton>

                                    {/* Show uploaded files */}
                                    {files
                                        .filter(
                                            (file) =>
                                                file.status === 'completed'
                                        )
                                        .map((attachedFile) => (
                                            <AttachmentItem
                                                key={`attachment-${attachedFile.id}`}
                                                file={{
                                                    id:
                                                        Number(
                                                            attachedFile.fileId
                                                        ) || generateUniqueId(),
                                                    name: attachedFile.file
                                                        .name,
                                                    type: attachedFile.file
                                                        .type,
                                                    fileId: Number(
                                                        attachedFile.fileId
                                                    )
                                                }}
                                                index={Number(
                                                    attachedFile.fileId
                                                )}
                                                removeAttachment={
                                                    removeAttachment
                                                }
                                            />
                                        ))}
                                </AttachmentsContainer>

                                {/* Upload progress */}
                                <UploadProgress
                                    files={files}
                                    onCancelUpload={cancelUpload}
                                />
                            </FormField>
                        </FormRow>
                        <FormRow>
                            <FormField>
                                <LabelTypography sx={{ textAlign: 'left' }}>
                                    Skills
                                </LabelTypography>

                                <FlexContainer>
                                    {skills.length > 0 ? (
                                        skills.map((skill, index) => (
                                            <SkillChip
                                                key={index}
                                                isHovered={
                                                    hoveredSkill === index
                                                }
                                                onMouseEnter={() =>
                                                    setHoveredSkill(index)
                                                }
                                                onMouseLeave={() =>
                                                    setHoveredSkill(null)
                                                }
                                            >
                                                <span>{skill.name}</span>
                                                <CloseIcon
                                                    fontSize="small"
                                                    style={CloseIconStyle(
                                                        hoveredSkill === index
                                                    )}
                                                    onClick={() =>
                                                        handleRemoveSkill(index)
                                                    }
                                                />
                                            </SkillChip>
                                        ))
                                    ) : (
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                            sx={{
                                                textAlign: 'left',
                                                fontStyle: 'italic',
                                                color: color.white
                                            }}
                                        >
                                            Add skills by clicking the Add
                                            button
                                        </Typography>
                                    )}
                                    <AddButton
                                        onClick={() => {
                                            setSkillDialogOpen(true);
                                        }}
                                        isHovered={isAddSkillHovered}
                                        onMouseEnter={() =>
                                            setIsAddSkillHovered(true)
                                        }
                                        onMouseLeave={() =>
                                            setIsAddSkillHovered(false)
                                        }
                                    >
                                        <span>Add</span>
                                        {isAddSkillHovered && (
                                            <AddIcon style={AddIconStyle} />
                                        )}
                                    </AddButton>
                                </FlexContainer>
                            </FormField>
                        </FormRow>

                        <FormRow>
                            <FormField>
                                <LabelTypography sx={{ textAlign: 'left' }}>
                                    Connect
                                </LabelTypography>

                                <FlexContainer>
                                    {connected && (
                                        <ConnectChipActive>
                                            <span>Github</span>
                                            <GithubIcon style={IconStyle} />
                                            <CheckIcon
                                                style={{
                                                    position: 'absolute',
                                                    top: '-4px',
                                                    right: '-4px',
                                                    width: '12px',
                                                    height: '12px',
                                                    fill: color.yellow
                                                }}
                                            />
                                        </ConnectChipActive>
                                    )}
                                </FlexContainer>
                            </FormField>
                        </FormRow>

                        <FormRow>
                            <FormField>
                                <LabelTypography sx={{ textAlign: 'left' }}>
                                    Team members
                                </LabelTypography>

                                <FlexContainer>
                                    {teamMembers.length > 0 ? (
                                        teamMembers.map((member, index) => (
                                            <TeamMemberChip
                                                key={index}
                                                onMouseEnter={() =>
                                                    setHoveredTeam(index)
                                                }
                                                onMouseLeave={() =>
                                                    setHoveredTeam(null)
                                                }
                                            >
                                                <span>{member.name}</span>
                                                <TeamMemberRole>
                                                    {member.role}
                                                </TeamMemberRole>
                                                <CloseIcon
                                                    fontSize="small"
                                                    style={CloseIconStyle(
                                                        hoveredTeam === index
                                                    )}
                                                    onClick={() =>
                                                        handleRemoveTeamMember(
                                                            index
                                                        )
                                                    }
                                                />
                                            </TeamMemberChip>
                                        ))
                                    ) : (
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                            sx={{
                                                textAlign: 'left',
                                                fontStyle: 'italic',
                                                color: color.white
                                            }}
                                        >
                                            Add team members by clicking the Add
                                            button
                                        </Typography>
                                    )}
                                    <AddButton
                                        onClick={() => setCurrentStep(3)}
                                        isHovered={isAddTeamHovered}
                                        onMouseEnter={() =>
                                            setIsAddTeamHovered(true)
                                        }
                                        onMouseLeave={() =>
                                            setIsAddTeamHovered(false)
                                        }
                                    >
                                        <span>Add</span>
                                        {isAddTeamHovered && (
                                            <AddIcon style={AddIconStyle} />
                                        )}
                                    </AddButton>
                                </FlexContainer>
                            </FormField>
                        </FormRow>

                        <ButtonContentBox>
                            <StyledButton
                                variant="contained"
                                fullWidth
                                type="submit"
                                onMouseEnter={() => setCreateIsHovered(true)}
                                onMouseLeave={() => setCreateIsHovered(false)}
                            >
                                <ButtonTextBox $isHovered={isCreateHovered}>
                                    Create
                                </ButtonTextBox>
                                <ButtonIconBox $isHovered={isCreateHovered}>
                                    🐝
                                </ButtonIconBox>
                            </StyledButton>
                        </ButtonContentBox>
                    </Form>
                )}
            </Formik>

            <SkillDialog
                open={skillDialogOpen}
                onClose={() => {
                    setSkillDialogOpen(false);
                }}
                onAddSkills={handleAddSkills}
                existingSkills={skills}
            />
        </ServicesCard>
    );
};

export default ProjectDetails;
